import React, { useState } from 'react';
import moment from 'moment';
import { DEFAULT_GRID_TABLE } from '@components-clean/core/api-grid/api-grid.helper';
import { IDataset } from '@pages-clean/central-monitoring/data-view/types';
import { DataReviewType } from 'types/graphql-global-types';
import ApiGrid from 'views-clean/components/core/api-grid';
import { useLocalState, useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromCurrentUtcDate } from '@utils/convertor';
import {
    dataReviewTables_OpraQueries_dataReviewTablesForStudy_DataReviewTable,
    dataReviewTables,
} from '@queries/generated/central-monitoring.generated';

type DataTableProps = {
    studyRef: string;
    dataset: IDataset;
    dataViewDataTableIds: string[];
    studyDataTables?: dataReviewTables;
    subjectId?: string | null;
    gridFilterable?: boolean;
    noPadding?: boolean;
    dataViewRef?: string | null;
    onSelectedDatatableChange?: (
        datatable: dataReviewTables_OpraQueries_dataReviewTablesForStudy_DataReviewTable | null | undefined,
    ) => void;
};

const DataTables = ({
    studyRef,
    dataset,
    dataViewDataTableIds,
    studyDataTables,
    subjectId,
    gridFilterable,
    noPadding,
    dataViewRef,
    onSelectedDatatableChange,
}: DataTableProps) => {
    const { state } = useLocalState();
    const study = state?.selectedStudy;
    const dataViewConfigDTs = dataViewDataTableIds?.map((dt) => {
        return studyDataTables?.dataReviewTablesForStudy?.find((item) => item?.id === dt);
    });
    const [selectedDatatable, setSelectedDatatable] = useState<
        dataReviewTables_OpraQueries_dataReviewTablesForStudy_DataReviewTable | null | undefined
    >(dataViewConfigDTs?.[0]);

    const { t } = useTranslation('cm');

    const filenameForExport = `${selectedDatatable?.name}_${study?.studyNumber}_Dataset ${moment(
        dataset.dataCutDate,
    ).format('DD-MMM-yyyy')}_Export date ${stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-yyyy')}`;

    return (
        <ApiGrid
            title={dataViewConfigDTs?.map((x) => ({
                ...x,
                id: x?.id as string,
                label: x?.name,
                active: x?.active,
            }))}
            selectedTitle={
                selectedDatatable && {
                    ...selectedDatatable,
                    id: selectedDatatable.id as string,
                    label: selectedDatatable.name,
                }
            }
            onTitleChange={(e) => {
                if (e.value) {
                    const selectedValue =
                        e.value as dataReviewTables_OpraQueries_dataReviewTablesForStudy_DataReviewTable;
                    setSelectedDatatable(selectedValue);
                    // Need to pass the selected value back up to the parent
                    onSelectedDatatableChange?.(selectedValue);
                }
            }}
            testDataId="datatables-grid"
            maxHeight="auto"
            table={DEFAULT_GRID_TABLE}
            gridParams={{
                grid: 'DataTable',
                studyRef: studyRef ?? null,
                dataset: dataset.id,
                datatable: selectedDatatable ? (selectedDatatable?.id as string) : '',
                subjectId: subjectId || null,
                isSubjectLevelTable: selectedDatatable?.dataSource === 'subjectsAll',
                dataViewRef: selectedDatatable?.type === DataReviewType.SITE ? dataViewRef : null,
            }}
            filterable={gridFilterable ?? selectedDatatable?.type !== DataReviewType.DATA}
            titleReadOnly={dataViewDataTableIds.length === 1}
            noTitleMessage={!selectedDatatable?.active ? t('dataView.datatables.inActiveDataTable') : undefined}
            fetchData={selectedDatatable?.active || false}
            screenFilterable={selectedDatatable?.type === DataReviewType.DATA}
            exportFilename={filenameForExport}
            showAddIcon={false}
            showPlaceholder={false}
            noPadding={noPadding}
            rowHeight={24}
            scrollable="virtual"
        />
    );
};

export default DataTables;
